import { merge } from "lib/merge";
import { useStoreFromContext } from "lib/store";
import { useEffect } from "react";
import { FormContext } from "./contexts";
import { validate, validateWithRules } from "./validate";

export function useField(name, rules = undefined) {
    const [form, setForm] = useStoreFromContext(FormContext);

    useEffect(() => {
        if (rules !== undefined) {
            setForm((state) => merge(state, { rules: { [name]: rules } }));
        }
    }, []);

    const onChange = (v) => {
        const e = form.alwaysValidate ? validateWithRules(v, form.rules?.[name]) : "";
        setForm((state) => {
            const s = merge(state, { values: { [name]: v }, errors: { [name]: e } });
            // notify listeners of the change
            form.onChange.forEach((h) => {
                if (typeof h === "function") {
                    h(s.values);
                }
            });
            return s;
        });
    };

    return [form.values[name] ?? "", form.errors[name] ?? "", onChange];
}

export function useForm(props, deps = []) {
    const [form, setForm] = useStoreFromContext(FormContext);

    useEffect(() => {
        if (typeof props !== "undefined") {
            const { onSubmit, onChange, ...rest } = props;
            setForm((state) => ({
                ...merge(state, rest),
                // add onSubmit listener to the collection
                onSubmit:
                    typeof onSubmit === "function" ? [...state.onSubmit, onSubmit] : state.onSubmit,
                // add onChange listener to the collection
                onChange:
                    typeof onChange === "function" ? [...state.onChange, onChange] : state.onChange
            }));
            return () => {
                // remove the old onSubmit listeners on component dismount.
                if (typeof onSubmit === "function") {
                    setForm((state) => ({
                        ...state,
                        onSubmit: state.onSubmit.filter((h) => h !== onSubmit)
                    }));
                }
                // remove the old onChange listeners on component dismount.
                if (typeof onChange === "function") {
                    setForm((state) => ({
                        ...state,
                        onChange: state.onChange.filter((h) => h !== onChange)
                    }));
                }
            };
        }
        return () => {};
    }, deps);

    const onSubmit = () => {
        const { errorCount, errors } = validate(form.values, form.rules);
        if (errorCount > 0) {
            setForm((state) => merge(state, { errors, error: "Validation failed" }));
            return;
        }
        setForm((state) => merge(state, { errors, error: "", submitting: true }));
        // notify listeners of the submit
        const promises = form.onSubmit.map((h) => {
            if (typeof h === "function") {
                return h(form.values);
            }
            return Promise.resolve();
        });
        Promise.all(promises)
            .then(() => {
                setForm((state) => merge(state, { submitting: false, submitted: true, error: "" }));
            })
            .catch((err) => {
                setForm((state) => merge(state, { submitting: false, error: err.message }));
            });
    };

    return [onSubmit, form.submitting, form.submitted, form.error];
}
